import type { AgentDescriptor, Director, EndSessionInput, Harness, MicroSession, RetainCandidate } from 'contexttrees';
import type { AgentPersona } from './personas';
import { sendMessageOnce, type AnthropicContentBlock } from '../api/anthropic';
import { SUMMARIZATION_MODEL } from '../api/pricing';

/**
 * The ContextTrees side of agent mode: persona->harness mirroring, and authoring the
 * `EndSessionInput` that `Director.endSession` reconciles back into the design map / agent memory.
 *
 * Nothing here is an enforcement path. `Harness.constraints` is descriptive `string[]` only —
 * `AgentPersona.canWritePath` stays an executable predicate enforced Naulon-side in `executeTool`
 * (see `ToolExecutionContext`). Registering a harness is bookkeeping so the design map knows what
 * the agent was allowed to do; it never decides what the agent is allowed to do.
 */

/**
 * Human-readable mirror of each persona's `canWritePath` predicate. Keyed by persona id rather than
 * derived from the function, because a predicate can't be serialized into a `string[]` faithfully —
 * if a persona's predicate changes, this string has to be updated alongside it by hand.
 */
const CONSTRAINT_DESCRIPTIONS: Record<string, string> = {
  'test-writer': 'write_file limited to test files (path must contain ".test." or ".spec.")',
  refactorer: 'write_file refuses test files (path containing ".test." or ".spec.")',
};

const GENERIC_WRITE_CONSTRAINT = 'write_file restricted to a subset of paths (enforced in executeTool)';

/**
 * Idempotent by design: re-registering an unchanged harness on every turn would churn the project
 * snapshot for nothing, so an existing registration is returned untouched.
 */
export function ensurePersonaHarness(director: Director, persona: AgentPersona): Harness {
  const existing = director.harnesses.get(persona.id);
  if (existing) return existing;

  return director.harnesses.register({
    id: persona.id,
    name: persona.label,
    tools: persona.allowedTools,
    systemPrompt: persona.systemPromptAddition,
    constraints: persona.canWritePath ? [CONSTRAINT_DESCRIPTIONS[persona.id] ?? GENERIC_WRITE_CONSTRAINT] : [],
  });
}

/** Fixed baseline: one session has no way to observe cross-session reuse, so it can't estimate it. */
const BASELINE_REUSE_SCORE = 0.5;

/**
 * Best-effort extraction of the distinct paths a turn wrote to, from the already-formatted tool-call
 * log lines (`` 🔧 `write_file({"path":"src/foo.ts",...})` ``). The input summary in those lines is
 * truncated to 100 chars, so it is frequently *not* valid JSON — hence a targeted `"path"` match
 * rather than a parse. Anything that doesn't match is skipped silently; a malformed log line must
 * never be able to fail a turn.
 */
function extractWrittenPaths(toolCallLog: string[]): string[] {
  const paths: string[] = [];
  for (const line of toolCallLog) {
    if (!line.includes('write_file(')) continue;
    const match = /"path"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(line);
    if (!match) continue;
    let path = match[1];
    try {
      path = JSON.parse(`"${match[1]}"`) as string;
    } catch {
      // Keep the raw capture — an unescapable fragment is still a usable concept label.
    }
    if (path && !paths.includes(path)) paths.push(path);
  }
  return paths;
}

/**
 * A `DesignMapNodeContent` update replaces the whole object, so "appending a roadmap bullet" means
 * spreading the current content forward. Reading `current()` here rather than taking it as an
 * argument keeps the two authoring paths from drifting.
 */
function appendRoadmapBullet(director: Director, rootNodeId: string, bullet: string): EndSessionInput['designMapUpdates'] {
  const current = director.designMap.current(rootNodeId);
  const dated = `${new Date().toISOString().slice(0, 10)}: ${bullet}`;
  return [{ nodeId: rootNodeId, content: { ...current, roadmap: [...current.roadmap, dated] } }];
}

export interface HeuristicReconciliationInput {
  session: MicroSession;
  agent: AgentDescriptor;
  toolCallLog: string[];
  description: string;
  rootNodeId: string;
  director: Director;
}

/** Zero extra API calls: everything is derived from what the turn already did. */
export function buildHeuristicReconciliation(input: HeuristicReconciliationInput): EndSessionInput {
  const { session, agent, toolCallLog, rootNodeId, director } = input;
  const paths = extractWrittenPaths(toolCallLog);

  const retain: RetainCandidate[] = paths.map((path) => ({
    concept: path,
    content: `Touched by ${session.description} (${toolCallLog.length} tool call(s))`,
    reuseScore: BASELINE_REUSE_SCORE,
  }));

  return {
    // The entry is emitted even with an empty `retain` so the shape stays uniform across turns.
    agentMemoryUpdates: [{ agentId: agent.id, retain }],
    designMapUpdates: appendRoadmapBullet(director, rootNodeId, session.description),
    activationUpdates: [],
  };
}

export interface LlmReconciliationInput extends HeuristicReconciliationInput {
  apiKey: string;
  finalText: string;
}

interface AuthoredReconciliation {
  roadmapNote: string;
  retain: { concept: string; content: string; reuseScore: number }[];
}

const AUTHORING_SYSTEM_PROMPT =
  'You reconcile a finished coding session into a project roadmap. ' +
  'Output ONLY raw JSON — no markdown fences, no preamble, no trailing commentary — matching exactly: ' +
  '{"roadmapNote": string, "retain": [{"concept": string, "content": string, "reuseScore": number}]}. ' +
  'roadmapNote is one short past-tense sentence describing what the session accomplished. ' +
  'Each retain entry captures one durable fact worth remembering in a later session: concept is a short ' +
  'key (usually a file path or component name), content is one factual sentence, and reuseScore is ' +
  'between 0 and 1 estimating how likely that fact is to matter again. Emit at most 6 retain entries, ' +
  'and an empty array if nothing durable was learned.';

/** Rejects anything not matching the documented shape, so a plausible-but-wrong parse can't get through. */
function validateAuthored(value: unknown): AuthoredReconciliation | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as { roadmapNote?: unknown; retain?: unknown };
  if (typeof candidate.roadmapNote !== 'string' || !candidate.roadmapNote.trim()) return null;
  if (!Array.isArray(candidate.retain)) return null;

  const retain: AuthoredReconciliation['retain'] = [];
  for (const item of candidate.retain) {
    if (typeof item !== 'object' || item === null) return null;
    const entry = item as { concept?: unknown; content?: unknown; reuseScore?: unknown };
    if (typeof entry.concept !== 'string' || !entry.concept.trim()) return null;
    if (typeof entry.content !== 'string') return null;
    if (typeof entry.reuseScore !== 'number' || !Number.isFinite(entry.reuseScore)) return null;
    retain.push({
      concept: entry.concept,
      content: entry.content,
      // A model asked for 0-1 will occasionally hand back 5 or -1; clamp rather than reject.
      reuseScore: Math.min(1, Math.max(0, entry.reuseScore)),
    });
  }
  return { roadmapNote: candidate.roadmapNote.trim(), retain };
}

/** Tolerates the model wrapping its JSON in a ```json fence despite being told not to. */
function stripJsonFence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith('```')) return trimmed;
  return trimmed
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/, '')
    .trim();
}

/**
 * One extra call on a cheap model (same pattern as the pipeline's D1 summarization hook) to author
 * a richer reconciliation than the heuristic can. Deliberately total: every failure mode — network,
 * bad JSON, wrong shape — falls back to `buildHeuristicReconciliation` rather than propagating,
 * because a bookkeeping call must never be able to break the user's actual conversation.
 */
export async function authorReconciliationWithLLM(input: LlmReconciliationInput): Promise<EndSessionInput> {
  const { apiKey, session, agent, toolCallLog, finalText, rootNodeId, director } = input;

  try {
    const prompt =
      `Session description:\n${session.description}\n\n` +
      `Tool calls made (${toolCallLog.length}):\n${toolCallLog.join('\n') || '(none)'}\n\n` +
      `Agent's final response:\n${finalText}`;

    const response = await sendMessageOnce(apiKey, {
      model: SUMMARIZATION_MODEL,
      max_tokens: 800,
      system: [{ type: 'text', text: AUTHORING_SYSTEM_PROMPT }],
      messages: [{ role: 'user', content: prompt }],
      stream: false,
    });

    const text = response.content
      .filter((b): b is Extract<AnthropicContentBlock, { type: 'text' }> => b.type === 'text')
      .map((b) => b.text)
      .join('');

    const authored = validateAuthored(JSON.parse(stripJsonFence(text)));
    if (!authored) throw new Error('Reconciliation JSON did not match the expected shape');

    return {
      agentMemoryUpdates: [
        {
          agentId: agent.id,
          retain: authored.retain.map((r) => ({ concept: r.concept, content: r.content, reuseScore: r.reuseScore })),
        },
      ],
      designMapUpdates: appendRoadmapBullet(director, rootNodeId, authored.roadmapNote),
      activationUpdates: [],
    };
  } catch (err) {
    console.warn('ContextTrees: LLM reconciliation authoring failed, falling back to heuristic.', err);
    return buildHeuristicReconciliation(input);
  }
}

/** One dispatched agent: which persona handles it, and the slice of the message it handles. */
export interface DispatchPlanEntry {
  personaId: string;
  task: string;
}

/**
 * Hard ceiling on the fan-out. Every extra agent is a full extra tool-use loop against the *expensive*
 * model, so an unbounded split would quietly multiply the cost of a turn — the opposite of this app's
 * reason to exist. Project Mode already accepts some extra spend; this bounds how much.
 */
export const MAX_DISPATCH_AGENTS = 3;

function buildDispatchSystemPrompt(personas: AgentPersona[]): string {
  const catalog = personas.map((p) => `"${p.id}": ${p.label} — ${p.description}`).join('\n');
  return (
    'You route an incoming developer message to one or more specialist coding agents. ' +
    'The available agents are:\n' +
    `${catalog}\n\n` +
    'Most messages need exactly one agent. Only split when the message genuinely contains independent ' +
    'sub-tasks that benefit from different specialists running in parallel (for example "refactor X and ' +
    'also write tests for Y"). Never emit more than ' +
    `${MAX_DISPATCH_AGENTS} entries. ` +
    'Output ONLY raw JSON — no markdown fences, no preamble, no trailing commentary — matching exactly: ' +
    '{"dispatch": [{"personaId": string, "task": string}]}. ' +
    'personaId must be one of the ids listed above. task is the self-contained instruction that agent ' +
    'should carry out; for a single-agent plan it is the original message verbatim.'
  );
}

/** Same rejection posture as `validateAuthored`: a plausible-but-wrong parse must not get through. */
function validateDispatch(value: unknown, validIds: Set<string>): DispatchPlanEntry[] | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as { dispatch?: unknown };
  if (!Array.isArray(candidate.dispatch) || candidate.dispatch.length === 0) return null;

  const plan: DispatchPlanEntry[] = [];
  for (const item of candidate.dispatch) {
    if (typeof item !== 'object' || item === null) return null;
    const entry = item as { personaId?: unknown; task?: unknown };
    if (typeof entry.personaId !== 'string' || !validIds.has(entry.personaId)) return null;
    if (typeof entry.task !== 'string' || !entry.task.trim()) return null;
    plan.push({ personaId: entry.personaId, task: entry.task.trim() });
  }
  return plan.slice(0, MAX_DISPATCH_AGENTS);
}

export interface DispatchPlanRequest {
  apiKey: string;
  message: string;
  personas: AgentPersona[];
  defaultPersonaId: string;
}

/**
 * The director's routing decision: one cheap-model call that turns a message into the list of agents
 * that should handle it. Deliberately total in the same way `authorReconciliationWithLLM` is — every
 * failure mode (network, bad JSON, wrong shape, unknown persona, empty plan) collapses to the single
 * manually-selected persona handling the whole message, which is byte-for-byte the pre-dispatch
 * behavior. Routing is an enhancement; it never gets to break or reshape the user's turn.
 */
export async function planAgentDispatch(input: DispatchPlanRequest): Promise<DispatchPlanEntry[]> {
  const { apiKey, message, personas, defaultPersonaId } = input;
  const fallback: DispatchPlanEntry[] = [{ personaId: defaultPersonaId, task: message }];

  try {
    const prompt =
      `The user manually selected "${defaultPersonaId}" as the default agent — prefer it when a single ` +
      `agent suffices and no other specialist is a clearly better fit.\n\nMessage:\n${message}`;

    const response = await sendMessageOnce(apiKey, {
      model: SUMMARIZATION_MODEL,
      max_tokens: 500,
      system: [{ type: 'text', text: buildDispatchSystemPrompt(personas) }],
      messages: [{ role: 'user', content: prompt }],
      stream: false,
    });

    const text = response.content
      .filter((b): b is Extract<AnthropicContentBlock, { type: 'text' }> => b.type === 'text')
      .map((b) => b.text)
      .join('');

    const plan = validateDispatch(JSON.parse(stripJsonFence(text)), new Set(personas.map((p) => p.id)));
    if (!plan || plan.length === 0) throw new Error('Dispatch JSON did not match the expected shape');
    return plan;
  } catch (err) {
    console.warn('ContextTrees: agent dispatch planning failed, falling back to the selected persona.', err);
    return fallback;
  }
}
